/**
 * BuildTaskStatusMessage · 建夹任务状态消息条 (独立 message entity)
 *
 * 作为 messages 数组里的一条独立 assistant message 出现, 跟 user / 其他 AI 回复
 * 按时间顺序排列。不依附于 FolderPlanPreview 卡片。两态:
 *
 *   · running · "正在创建文件夹"               · indigo
 *   · done    · "已为你创建 N 个智能文件夹"     · green
 *
 * 数据流 (组件自查模式):
 *   · message 由 messageAppended 推入 store, card 仅携带 taskId
 *   · 组件挂载后 useBuildTaskDetail(taskId) 立即拉一次 /ai/chat/task, 非终态
 *     自动 3s 轮询; 终态停止
 *   · 渲染依据 detail.status; N = detail.top_folders.length (后端任务级真相)
 *     · 兼容旧返回缺 top_folders 时退化到 detail.done
 *
 * 为什么不进 Redux:
 *   · 状态唯一消费方就是本组件, 没必要进 store 中转 (前一版 buildTaskStatus
 *     MessageStatusUpdated reducer + selection.buildTaskId + 顶层 polling hook
 *     这套链路全被本 hook 取代)
 *   · 多任务历史会话每条 message 独立挂 hook → 并行先到先显示, 不被 Promise.all
 *     阻塞 (上一版 hydrateTaskMessages 设计的痛点)
 *
 * 失败 / 取消态本期不在 UI 渲染 (return null), 后续视需要再补。
 *
 * 首次请求未到达 (loading && !detail) 的占位策略:
 *   · live 路径 (card.initialStatus 未传) → "正在创建文件夹" 运行态占位
 *   · history 已完成任务 (card.initialStatus === 'done') → 完成态骨架: 绿色
 *     容器 + check 图标 + 数字位置灰色 placeholder, 拉到详情后填上 N
 *
 * 区分两种占位是为了消除 history 回看时闪过 "正在创建文件夹" 造成的误导 —
 * 这条任务在 history 接口的 data.card.status 里已经能确认是 done 了, 真正
 * 没拿到的只是 top_folders.length 这一个数字。
 */

import React, { useEffect, useRef } from 'react'
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons'
import { useAppDispatch } from '../../../../../../../app/hooks'
import { bumpFolderRefreshTick } from '../../../../../../../slices/aiClassify'
import type {
  BuildTaskDetail,
  BuildTaskStatusCard
} from '../../../../../../../types/ai-classify'
import { useBuildTaskDetail } from '../../../../../hooks/useBuildTaskDetail'
import styles from './BuildTaskStatusMessage.module.scss'

export interface BuildTaskStatusMessageProps {
  /** 仅 taskId 字段 · CardSwitch 'build_task_status' 分支注入 */
  card: BuildTaskStatusCard
}

const BuildTaskStatusMessage: React.FC<BuildTaskStatusMessageProps> = ({
  card
}) => {
  const { detail, loading } = useBuildTaskDetail(card.taskId)

  // "目睹 running/pending → done" 时刷一次 FolderList, 让用户看到新建文件夹。
  //
  // 为什么这条副作用放在 BuildTaskStatusMessage 而不是 useBuildTaskDetail:
  //   · hook 是纯查询服务 (给 taskId 给 detail), 不该耦合"我作为 message 渲染
  //     者关心 FolderList"这种业务策略 — 如果以后别的页面也用 useBuildTaskDetail
  //     但不关心 FolderList, 那条副作用就成了越权坑
  //   · "目睹过 running 才刷" 的判定也属于本组件的渲染语义 (历史回放打开就是
  //     done, 没目睹过 running, 不该刷; 旧实现把它塞进 hook 里导致每次切会话
  //     都误刷)
  //
  // 防重: 同一 taskId 一旦刷过, 标记到 refreshedRef, 后续 done 再来不重刷。
  const dispatch = useAppDispatch()
  const prevStatusRef = useRef<BuildTaskDetail['status'] | null>(null)
  const refreshedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!detail) return
    const prev = prevStatusRef.current
    prevStatusRef.current = detail.status
    if (detail.status !== 'done') return
    const sawRunningBefore = prev === 'pending' || prev === 'running'
    if (!sawRunningBefore) return
    if (refreshedRef.current.has(card.taskId)) return
    refreshedRef.current.add(card.taskId)
    dispatch(
      bumpFolderRefreshTick({ newIds: [], preserveRecentlyCreated: true })
    )
  }, [detail, card.taskId, dispatch])

  // 首次未拿到 + 仍在 loading → 按 card.initialStatus 决定占位形态
  // (累计失败放弃时 loading 已 false 且 detail==null, 走下面 return null 静默)
  if (!detail) {
    if (loading) {
      return card.initialStatus === 'done'
        ? renderDoneSkeleton()
        : renderRunning()
    }
    return null
  }

  const status = detail.status
  if (status === 'pending' || status === 'running') {
    return renderRunning()
  }
  if (status === 'done') {
    const count = Array.isArray(detail.top_folders)
      ? detail.top_folders.length
      : (detail.done ?? 0)
    return renderDone(count)
  }
  // failed / canceled · 本期不渲染
  return null
}

function renderRunning(): React.ReactElement {
  return (
    <div className={`${styles.statusCard} ${styles.statusCardRunning}`}>
      <span className={`${styles.statusIcon} ${styles.statusIconRunning}`}>
        <LoadingOutlined />
      </span>
      <span className={styles.statusText}>正在创建文件夹</span>
    </div>
  )
}

function renderDone(count: number): React.ReactElement {
  return (
    <div className={`${styles.statusCard} ${styles.statusCardDone}`}>
      <span className={`${styles.statusIcon} ${styles.statusIconDone}`}>
        <CheckOutlined />
      </span>
      <span className={styles.statusText}>已为你创建 {count} 个智能文件夹</span>
    </div>
  )
}

/**
 * 历史已完成任务首拉中的完成态骨架: 跟 renderDone 同容器 + 同 check 图标,
 * 整段文本用 shimmer 矩形覆盖。详情拉到后容器/图标不变, 仅文本替换为真实
 * "已为你创建 N 个智能文件夹", 视觉无跳变。
 */
function renderDoneSkeleton(): React.ReactElement {
  return (
    <div className={`${styles.statusCard} ${styles.statusCardDone}`}>
      <span className={`${styles.statusIcon} ${styles.statusIconDone}`}>
        <CheckOutlined />
      </span>
      <span className={styles.statusTextSkeleton} aria-hidden />
    </div>
  )
}

export default BuildTaskStatusMessage
